import { Crosshair, LocateFixed, MapPin, Trash2 } from "lucide-react";
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useEffect, useRef, useState } from "react";
import { normalizeBusinessLocationPin, type BusinessLocationPin } from "./business-location-pin";
import "./tenant-location-picker.css";

export type { BusinessLocationPin } from "./business-location-pin";

interface TenantLocationPickerProps {
  value: BusinessLocationPin | null;
  onChange: (value: BusinessLocationPin | null) => void;
}

const DEFAULT_CENTER: L.LatLngExpression = [27.7172, 85.324];
const DEFAULT_ZOOM = 13;
const PIN_ZOOM = 17;

const BUSINESS_PIN_ICON = L.divIcon({
  className: "tenant-location-picker__marker",
  html: '<span aria-hidden="true"><span></span></span>',
  iconAnchor: [22, 51],
  iconSize: [44, 52],
});

export function TenantLocationPicker({ value, onChange }: TenantLocationPickerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const onChangeRef = useRef(onChange);
  const [locating, setLocating] = useState(false);
  const [message, setMessage] = useState("Click or tap the map to place your business pin.");
  const [messageTone, setMessageTone] = useState<"neutral" | "success" | "error">("neutral");

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || mapRef.current) return undefined;

    const map = L.map(container, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      scrollWheelZoom: false,
      zoomControl: true,
    });
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      detectRetina: true,
      maxZoom: 19,
    }).addTo(map);
    map.on("click", (event: L.LeafletMouseEvent) => {
      onChangeRef.current(normalizeBusinessLocationPin(event.latlng.lat, event.latlng.lng));
      setMessage("Pin selected. It will be saved with your application.");
      setMessageTone("success");
    });
    mapRef.current = map;

    const resizeFrame = window.requestAnimationFrame(() => {
      map.invalidateSize();
    });
    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            map.invalidateSize({ pan: false });
          });
    resizeObserver?.observe(container);

    return () => {
      window.cancelAnimationFrame(resizeFrame);
      resizeObserver?.disconnect();
      markerRef.current = null;
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (!value) {
      markerRef.current?.remove();
      markerRef.current = null;
      return;
    }

    const position = L.latLng(value.latitude, value.longitude);
    if (!markerRef.current) {
      const marker = L.marker(position, {
        alt: "Selected business location",
        draggable: true,
        icon: BUSINESS_PIN_ICON,
        keyboard: true,
        riseOnHover: true,
        title: "Selected business location",
      }).addTo(map);
      marker.bindTooltip("Selected business location", {
        className: "tenant-location-picker__tooltip",
        direction: "top",
        offset: [0, -42],
      });
      marker.on("dragend", () => {
        const moved = marker.getLatLng();
        onChangeRef.current(normalizeBusinessLocationPin(moved.lat, moved.lng));
        setMessage("Pin moved. It will be saved with your application.");
        setMessageTone("success");
      });
      markerRef.current = marker;
    } else {
      markerRef.current.setLatLng(position);
    }
  }, [value]);

  const useCurrentLocation = () => {
    if (!("geolocation" in navigator)) {
      setMessage("Location access is unavailable in this browser. Place the pin manually.");
      setMessageTone("error");
      return;
    }

    setLocating(true);
    setMessage("Requesting your current location…");
    setMessageTone("neutral");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const nextValue = normalizeBusinessLocationPin(
          position.coords.latitude,
          position.coords.longitude,
        );
        onChangeRef.current(nextValue);
        mapRef.current?.flyTo([nextValue.latitude, nextValue.longitude], PIN_ZOOM, {
          duration: 0.8,
        });
        setMessage(
          "Current location found. Move the pin to the exact business entrance if needed.",
        );
        setMessageTone("success");
        setLocating(false);
      },
      () => {
        setMessage(
          "We could not access your current location. Check browser permissions or place the pin manually.",
        );
        setMessageTone("error");
        setLocating(false);
      },
      { enableHighAccuracy: true, maximumAge: 30_000, timeout: 12_000 },
    );
  };

  const clearPin = () => {
    onChange(null);
    setMessage("Pin cleared. Click or tap the map to choose another location.");
    setMessageTone("neutral");
  };

  const placePinAtMapCenter = () => {
    const center = mapRef.current?.getCenter();
    if (!center) return;
    onChange(normalizeBusinessLocationPin(center.lat, center.lng));
    setMessage("Pin selected at the map center. Move the map and place it again if needed.");
    setMessageTone("success");
  };

  return (
    <section className="tenant-location-picker" aria-labelledby="business-location-picker-title">
      <header className="tenant-location-picker__header">
        <span className="tenant-location-picker__icon" aria-hidden="true">
          <MapPin />
        </span>
        <div>
          <p className="eyebrow">MAP LOCATION</p>
          <h3 id="business-location-picker-title">Pin your business entrance</h3>
          <p>Help customers find the exact entrance. Your written address stays unchanged.</p>
        </div>
      </header>

      <div className="tenant-location-picker__actions">
        <button
          className="button button--outline"
          type="button"
          disabled={locating}
          onClick={useCurrentLocation}
        >
          <LocateFixed aria-hidden="true" />
          {locating ? "Finding your location…" : "Use current location"}
        </button>
        <button className="button button--outline" type="button" onClick={placePinAtMapCenter}>
          <Crosshair aria-hidden="true" /> Place pin at map center
        </button>
        <button className="button button--quiet" type="button" disabled={!value} onClick={clearPin}>
          <Trash2 aria-hidden="true" /> Clear pin
        </button>
      </div>

      <div className="tenant-location-picker__map-shell">
        <div
          className="tenant-location-picker__map"
          ref={containerRef}
          role="region"
          aria-label="Map for pinning the business location"
        />
        {!value && (
          <div className="tenant-location-picker__hint" aria-hidden="true">
            <MapPin />
            <span>Select a point on the map</span>
          </div>
        )}
      </div>

      <p
        className={`tenant-location-picker__message tenant-location-picker__message--${messageTone}`}
        role={messageTone === "error" ? "alert" : "status"}
        aria-live="polite"
      >
        {message}
      </p>

      {value && (
        <div className="tenant-location-picker__coordinates" aria-label="Selected coordinates">
          <div>
            <span>Latitude</span>
            <output>{value.latitude.toFixed(6)}</output>
          </div>
          <div>
            <span>Longitude</span>
            <output>{value.longitude.toFixed(6)}</output>
          </div>
        </div>
      )}
    </section>
  );
}
